import React, { useState } from "react";
import { toast } from "sonner";
import { Trophy, Mail, Check, AlertCircle, Copy, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { CITIES } from "../lib/cities";
import { useNavigate, Link } from "react-router-dom";

export default function Profile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [city, setCity] = useState(user?.city || "");
  const [optIn, setOptIn] = useState(!!user?.leaderboard_opt_in);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.patch("/auth/profile", { name, city, leaderboard_opt_in: optIn }); toast.success("Saved."); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold font-display">Profile</h1>
        <p className="text-sm text-gray-500 mt-1">Your account details.</p>
      </div>

      {/* PRE-PHASE 1: KYC Status Banner */}
      {user?.kyc_status === "verified" ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
          <Check className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-emerald-900">KYC Verified ✓</div>
            <p className="text-xs text-emerald-700 mt-1">Your identity has been verified. You can create agreements, pay deposits, and withdraw earnings.</p>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Complete your KYC to unlock all features</div>
            <p className="text-xs text-amber-700 mt-1">KYC (Know Your Customer) verification is required to create agreements, pay deposits, and receive payouts. It takes just 2 minutes.</p>
            <button
              onClick={() => nav("/onboarding/kyc")}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              Start KYC Verification →
            </button>
          </div>
        </div>
      )}

      {/* My Public Profile card */}
      {user?.slug && (
        <PublicProfileCard slug={user.slug} />
      )}

      <form onSubmit={save} className="card-base p-6 space-y-4">
        <div>
          <label className="text-sm font-medium">Name</label>
          <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-sm font-medium">Email</label><div className="mt-1 px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-700">{user?.email || "—"}</div></div>
          <div><label className="text-sm font-medium">Mobile</label><div className="mt-1 px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-700">{user?.phone}</div></div>
        </div>
        <div><label className="text-sm font-medium">Role</label><div className="mt-1 px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-700 capitalize">{user?.role?.replace("_"," ")}</div></div>

        {(user?.role === "agent" || user?.role === "broker_admin") && (
          <div>
            <label className="text-sm font-medium">City</label>
            <select data-testid="profile-city" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white">
              <option value="">— Select your city —</option>
              {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">Your local area for the points leaderboard{user?.role === "agent" ? " and the public Find an Agent page" : ""}.</p>
          </div>
        )}

        {user?.role === "agent" && (
          <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer" data-testid="optin-card">
            <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-1 h-4 w-4" data-testid="profile-optin" />
            <div className="flex-1">
              <div className="text-sm font-medium flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> Show me on the public leaderboard & finder</div>
              <p className="text-xs text-gray-500 mt-1">Only your first name + last initial and city are shown. No phone. No money. Resets monthly. Tenants can request you via the public Find an Agent page.</p>
            </div>
          </label>
        )}

        <button data-testid="profile-save" disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-5 py-2.5 rounded-lg font-medium text-sm disabled:opacity-50">Save</button>
      </form>

      {user?.role === "agent" && optIn && <LeadInbox />}
    </div>
  );
}

function PublicProfileCard({ slug }) {
  const [copied, setCopied] = useState(false);
  const url = `https://orello.in/u/${slug}`;
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="card-base p-5 space-y-3">
      <div className="flex items-center gap-2">
        <ExternalLink className="h-4 w-4 text-[#F97316]" />
        <span className="text-sm font-semibold">My Public Profile</span>
      </div>
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
        <span className="text-sm text-gray-600 flex-1 truncate">{url}</span>
        <button
          onClick={copy}
          className="flex items-center gap-1.5 text-xs font-medium text-[#F97316] hover:text-[#EA580C] shrink-0"
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <div className="flex gap-2">
        <Link
          to={`/u/${slug}`}
          target="_blank"
          className="flex items-center gap-1.5 text-xs font-medium bg-[#F97316] hover:bg-[#EA580C] btn-orange-primary text-white px-4 py-2 rounded-lg transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" /> View My Profile
        </Link>
      </div>
    </div>
  );
}

function LeadInbox() {
  const [leads, setLeads] = useState([]);
  React.useEffect(() => { (async () => { try { const { data } = await api.get("/leads"); setLeads(data); } catch {} })(); }, []);
  if (leads.length === 0) return <div className="card-base p-6 text-sm text-gray-500"><Mail className="h-4 w-4 inline mr-2" /> No leads yet. Tenants who request you on Find an Agent will show up here.</div>;
  return (
    <div className="card-base" data-testid="leads-inbox">
      <div className="px-5 py-4 border-b border-gray-100 font-semibold">Leads <span className="text-xs text-gray-400 font-normal">({leads.length})</span></div>
      <ul className="divide-y">
        {leads.map(l => (
          <li key={l.id} className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="font-medium">{l.lead_name}</div>
              <a href={`tel:${l.lead_phone}`} className="text-sm text-[#F97316]">{l.lead_phone}</a>
            </div>
            {l.requirement && <p className="text-sm text-gray-600 mt-1">{l.requirement}</p>}
            <div className="text-xs text-gray-400 mt-1">{l.city || "—"} · {new Date(l.created_at).toLocaleString("en-IN")}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
